import React from 'react';
import { DropzoneArea } from 'material-ui-dropzone';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import { deleteFiles, uploadFile } from '../API/files';
import { useSnackbarContext } from '../../contexts/Snackbar';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import UploadedFilesToShow from './UploadedFilesShow';

const useStyles = makeStyles((theme: any) => ({
  dropZone: {
    pointerEvents: ({ loading }: any) => {
      return loading ? 'none' : 'auto';
    },
    backgroundColor: 'transparent',
    border: 'none',
    minHeight: 'auto',
    '& .MuiSvgIcon-root': {
      display: 'none',
    },
  },
  dropzoneParagraph: {
    fontSize: '12px',
    margin: 0,
  },
  button: {
    color: theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}`,
    boxSizing: 'border-box',
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.brand.white,
    fontWeight: 700,
    width: '40%',
    alignSelf: 'center',
    fontSize: '12px',
    marginLeft: theme.spacing(2),
  },
  card: {
    minWidth: '100%',
    marginTop: theme.spacing(2),
    backgroundColor: theme.palette.primary.light,
    padding: theme.spacing(1),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.brand.blueSky}`,
    boxSizing: 'border-box',
    display: 'flex',
  },
  text: {
    color: theme.palette.brand.greenDark,
    fontSize: '12px',
    fontWeight: 400,
    width: '60%',
    textAlign: 'initial',
  },
}));

export const deleteAllFiles = async (ids) => {
  await deleteFiles(ids);
};

export default function UploadCard({
  props,
  optionalDoc,
  files,
  setFiles,
  setIds,
  ids,
  loading,
  setLoading,
  changes,
  setChangedRequestInfo,
}: any) {
  const classes = useStyles({ loading });
  const snackbar = useSnackbarContext();

  const deleteFile = async (file) => {
    const fileToDelete = ids.find(
      (fileInFiles) => file.fileName === fileInFiles.name,
    );
    setLoading(true);
    await deleteFiles([fileToDelete]).then(() => {
      setIds([...ids.filter((id) => id !== fileToDelete)]);
      setFiles([...files.filter((fileInFiles) => fileInFiles !== file)]);
      setLoading(false);
    });
  };

  const uploadFiles = async (filesToUpload) => {
    // validation that we are not sending more than 10 files in one request
    if (files.length > 9) {
      return snackbar.show('Reach limit file upload number ', 'error');
    }
    let filesSize = 0;
    files.forEach((file) => (filesSize += file.size));

    const fileOver10Mb = filesSize + filesToUpload.size >= 10000000;
    if (fileOver10Mb) {
      return snackbar.show('Reach limit size of files upload  ', 'error');
    }
    setLoading(true);
    filesToUpload.loading = true;
    setFiles((prev) => {
      return [...prev, filesToUpload];
    });
    await uploadFile(filesToUpload).then((file) => {
      setIds((prev) => {
        if (changes && setChangedRequestInfo) {
          setChangedRequestInfo({
            ...changes,
            files: [...prev, file[0]],
          });
        }
        return [...prev, file[0]];
      });

      setFiles((prev) => {
        const updatedFiles = prev.map((file) => {
          if (file.name === filesToUpload.name) {
            file.loading = false;
          }
          return file;
        });

        const removeLoadingIndicator = updatedFiles.find(
          (file) => file.loading === true,
        );
        setLoading(removeLoadingIndicator ? true : false);
        return [...updatedFiles];
      });
    });
  };

  return (
    <>
      <Box boxShadow={2} className={classes.card}>
        <Typography className={classes.text}>
          <span style={{ fontWeight: 'bold' }}>
            {optionalDoc ? 'Optional: ' : 'Required: '}
          </span>
          Attach supporting documentation for the change you'd like to make.
          E.g. a revised PO or invoice, bank statement, sales graph, etc.
        </Typography>
        <Button className={classes.button}>
          <DropzoneArea
            dropzoneClass={classes.dropZone}
            dropzoneText={'Upload a document'}
            onDrop={uploadFiles}
            onDelete={deleteFile}
            showAlerts={false}
            filesLimit={50}
            acceptedFiles={[
              'image/jpeg',
              'image/jpg',
              'image/png',
              'application/pdf',
            ]}
            dropzoneParagraphClass={classes.dropzoneParagraph}
            showPreviewsInDropzone={false}
            showPreviews={false}
            {...props}
          />
        </Button>
      </Box>
      <UploadedFilesToShow
        files={files}
        loading={loading}
        deleteFile={deleteFile}
      />
    </>
  );
}
